import { KMenuButton, KTagWithIcon, KText } from '@/ui-library/components';
import {
  Avatar,
  Box,
  Center,
  Flex,
  Icon,
  Link,
  MenuItem,
  useDisclosure,
} from '@chakra-ui/react';
import { Recording01 } from '@kula-ai/icons/solid/Recording01';
import { type InterviewType } from '../types/interview';
import { useHistory } from 'react-router-dom';
import { JoinInterviewButton } from './JoinInterviewButton';
import { getInterviewTime, locationIconMap } from '../utils';
import { SlashDivider } from '@kula-ai/icons/outline/SlashDivider';
import { Stars02 } from '@kula-ai/icons/dual-tone/Stars02';
import { Clock } from '@kula-ai/icons/dual-tone/Clock';
import { LayersThree02 } from '@kula-ai/icons/dual-tone/LayersThree02';
import { InfoCircle } from '@kula-ai/icons/dual-tone/InfoCircle';
import { Edit05 } from '@kula-ai/icons/dual-tone/Edit05';
import { XClose } from '@kula-ai/icons/dual-tone/XClose';
// import { SendMessageTrigger } from './SendMessageTrigger';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import { TransferredBadge } from '@/components/candidate-job-slider-v1/components/common/TransferredBadge';
import { InterviewIntelligenceStatusTag } from './InterviewIntelligenceStatusTag';
import { InterviewStatusTag } from './InterviewStatusTag';
import { useCameFromPath } from '@/hooks/usePreviousLocation';
import { Scheduler } from '@/components/shared/interview-scheduler/Scheduler';
import { ModalWithForm } from '@/components/ats/modal/ModalWithForm';
import { useDeleteInterviewMutation } from '@/store/query/scheduling';
import { useDateFormat, useShowToast } from '@/hooks';
import { useEffect, useRef, useState } from 'react';
import { KPopover } from '@/ui-library/components/organisms/KPopover/KPopover';
import { MeetingLinkDetails } from './MeetingLinkDetails';
import { useSelector } from 'react-redux';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useKFlags } from '@/hooks/use-k-flags';
import { ScheduleInterview } from '@/pages/InterviewManager/ScheduleInterview';
import { KDrawer } from '@/ui-library/components';
import { useQuery } from '@tanstack/react-query';
import { API_SERVICE } from '@/api/candidate-job-slider';
import {
  APPLICATION_QUERY_STALE_MS,
  getApplicationQueryKey,
} from '@/components/candidate-job-slider-v1/hooks/useApplicationHelper';
dayjs.extend(relativeTime);

interface InterviewHeaderProps {
  interview: InterviewType;
  refetch: () => void;
  interviewState: any;
}

export const InterviewHeader = ({
  interview,
  refetch,
  interviewState,
}: InterviewHeaderProps) => {
  const history = useHistory();
  const cameFromInterviews = useCameFromPath('/interviews');
  const {
    meeting_link,
    candidate,
    transfer_info,
    intelligence_status,
    meeting_status,
    ats_job,
    ats_application_id,
    ats_job_stage_id,
    ats_job_stage_action_id = null,
    can_edit,
    start_time,
    end_time,
    ats_job_stage_name,
  } = interview;

  const { feSchedulingRevampV2 } = useKFlags() as {
    feSchedulingRevampV2?: boolean;
  };

  const {
    isBeforeInterview,
    isAfterInterview,
    isDuringInterview,
    interviewInfoActions,
    hideInterviewInfoInToolbar,
  } = interviewState;

  const isNoteTaker = intelligence_status !== 'pending';
  const isCancelledInterview = meeting_status === 'cancelled';

  const [deleteInterview, { isLoading: isCancelling }] =
    useDeleteInterviewMutation();
  const editInterviewActions = useDisclosure();
  const cancelInterviewActions = useDisclosure();
  const toast = useShowToast();
  const rightSectionRef = useRef<HTMLDivElement>(null);
  const intelligenceStatusRef = useRef<HTMLDivElement>(null);
  const interviewStatusRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  const [now, setNow] = useState(dayjs());

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(dayjs());
    }, 10000); // Update every 10 seconds for accurate countdown
    return () => clearInterval(timer);
  }, []);

  const availableWidth =
    (headerRef.current?.clientWidth || 1000) -
    (intelligenceStatusRef.current?.clientWidth || 100) -
    (interviewStatusRef.current?.clientWidth || 100) -
    (rightSectionRef.current?.clientWidth || 100) -
    150;

  const handleBreadcrumbClick = () => {
    const hasPreviousHistory = history.length > 1;

    if (cameFromInterviews && hasPreviousHistory) {
      // If user came from interviews section and has history, go back
      history.goBack();
    } else {
      // If user came from outside interviews or has no history, go to upcoming
      history.push('/interviews/upcoming');
    }
  };

  const showInterviewSuccessToast = (apiReference) => {
    const msg = `Interview ${
      apiReference === 'create' ? 'created' : 'updated'
    } successfully`;
    toast({ title: msg });
  };

  const { dateFormat, timeFormat } = useDateFormat();
  const currUser = useSelector(selectCurrUser);

  const interviewTimeResult = getInterviewTime({
    startTime: start_time,
    endTime: end_time,
    timeFormat: timeFormat,
    dateFormat: dateFormat.label,
    currUserTimezone: currUser?.time_zone,
  });

  const menuItems = [
    hideInterviewInfoInToolbar && {
      id: 'interview_info',
      label: 'Interview info',
      icon: InfoCircle,
      onClick: interviewInfoActions?.onOpen,
    },
    can_edit && {
      id: 'edit_interview',
      label: 'Edit interview',
      icon: Edit05,
      onClick: () => {
        editInterviewActions.onOpen();
      },
    },
    !isCancelledInterview &&
      can_edit && {
        id: 'cancel_interview',
        label: 'Cancel interview',
        icon: XClose,
        onClick: () => {
          cancelInterviewActions.onOpen();
        },
      },
  ].filter(Boolean);

  const handleCancelInterview = async () => {
    const result = (await deleteInterview({ interviewId: interview.id })) as {
      error: [];
    };
    if (result?.error?.length) {
      toast({ title: 'Cancelling the interview failed', status: 'error' });
    } else {
      toast({ title: 'The interview has been canceled.' });
      const hasHistoryToGoBack = history.length > 1;
      if (cameFromInterviews && hasHistoryToGoBack) {
        history.goBack();
      } else {
        history.push('/interviews/upcoming');
      }
    }
  };

  // The new wizard derives the candidate phone from `applicationData.contact`
  // (`phone_code` + `phone_number`). The interview-detail response omits those
  // structured fields, so without this fetch the Candidate phone field renders
  // empty when Location=Phone Interview, even though the contact has one on
  // file. Shared cache key dedupes with the slider/list prefetch.
  const applicationIdStr =
    ats_application_id !== null && ats_application_id !== undefined
      ? String(ats_application_id)
      : null;
  const { data: applicationData } = useQuery<Record<string, unknown>>({
    queryKey: getApplicationQueryKey(applicationIdStr),
    queryFn: () =>
      API_SERVICE.getApplication(applicationIdStr as string, {
        include: ['stage_actions'],
      }),
    staleTime: APPLICATION_QUERY_STALE_MS,
    enabled:
      !!feSchedulingRevampV2 &&
      editInterviewActions.isOpen &&
      !!applicationIdStr,
    refetchOnWindowFocus: false,
  });

  const renderModals = () => {
    return (
      <>
        {editInterviewActions.isOpen && feSchedulingRevampV2 && (
          <KDrawer
            isOpen={true}
            onClose={editInterviewActions?.onClose}
            handleOutsideClose={() => {}}
            variant="full"
            contentProps={{ maxW: '90%' }}
            skipHeader
            skipFooter
          >
            <ScheduleInterview
              mode="manual"
              apiReference="edit"
              applicationId={ats_application_id}
              applicationData={applicationData}
              jobId={ats_job?.id}
              stageId={ats_job_stage_id}
              stageActivityId={ats_job_stage_action_id}
              interviewId={interview.id}
              candidateId={candidate?.id}
              candidateName={candidate?.name}
              candidateAvatar={candidate?.avatar ?? undefined}
              candidateEmail={candidate?.email}
              jobTitle={ats_job?.name}
              stageName={ats_job_stage_name}
              onClose={editInterviewActions?.onClose}
              onSuccess={() => {
                refetch();
                editInterviewActions?.onClose();
              }}
            />
          </KDrawer>
        )}
        {editInterviewActions.isOpen && !feSchedulingRevampV2 && (
          <Scheduler
            scheduleType="manual"
            apiReference="edit"
            isAdhoc={!ats_job_stage_action_id}
            jobId={ats_job?.id}
            candidateId={candidate?.id}
            stageId={ats_job_stage_id}
            stageActivityId={ats_job_stage_action_id}
            interviewId={interview.id}
            applicationId={ats_application_id}
            closeModal={editInterviewActions?.onClose}
            onSuccess={(apiReference) => {
              showInterviewSuccessToast(apiReference);
              editInterviewActions?.onClose();
              refetch();
            }}
          />
        )}
        {cancelInterviewActions.isOpen && (
          <ModalWithForm
            withForm={false}
            isOpen={cancelInterviewActions?.isOpen}
            onClose={cancelInterviewActions?.onClose}
            headerText="Cancel interview?"
            subHeaderText="This will remove the interview from the calendars of the candidate and interviewers"
            primaryCtaText="Cancel interview"
            handleSubmit={handleCancelInterview}
            isCtaLoading={isCancelling}
          />
        )}
      </>
    );
  };

  const rightHeaderMenu = () => {
    if (isCancelledInterview) return null;

    const showJoinInterviewButton =
      meeting_link &&
      (isBeforeInterview || isDuringInterview) &&
      !isAfterInterview;
    // const showSendMessageTrigger = isAfterInterview && !isDuringInterview;
    const showMoreActions = menuItems.length > 0;

    return (
      <Flex gap="12px" ref={rightSectionRef}>
        {showJoinInterviewButton && (
          <JoinInterviewButton interview={interview} />
        )}
        {/* {showSendMessageTrigger && (
          <SendMessageTrigger interview={interview} isHeader />
        )} */}
        {showMoreActions && (
          <KMenuButton
            variant="tertiary"
            triggerContent="More"
            menuContent={menuItems.map((item) => (
              <MenuItem
                key={item.id}
                onClick={item.onClick}
                _hover={{ fontWeight: 'semibold', bg: 'secondary-50' }}
                gap="8px"
              >
                {item.icon && (
                  <Icon
                    as={item.icon}
                    width="16px"
                    height="16px"
                    color="secondary-600"
                  />
                )}
                <KText fontWeight="inherit" size="sm-regular">
                  {item.label}
                </KText>
              </MenuItem>
            ))}
          />
        )}
      </Flex>
    );
  };

  // breadcrumb on the right + more actions on the right (rightHeaderMenu)
  const firstLevel = () => {
    return (
      <Flex justifyContent="space-between" alignItems="flex-start" minH="32px">
        {/* left section */}
        <Flex alignItems="center" gap="6px">
          <Icon as={Recording01} color="secondary-600" />
          <KText
            variant="heading_3"
            color="secondary-600"
            cursor="pointer"
            onClick={handleBreadcrumbClick}
          >
            Interviews /
          </KText>
        </Flex>
        {rightHeaderMenu()}
      </Flex>
    );
  };

  const candidateLink = (() => {
    // is_current marks the candidate's live application (the active leaf of the chain, resolved
    // server-side), so this links straight to their current job for multi-hop transfers too.
    if (transfer_info?.history?.length && transfer_info?.applications) {
      const currentAppId = transfer_info.history.find(
        (appId) => transfer_info.applications[appId]?.is_current
      );
      if (currentAppId) {
        const currentApp = transfer_info.applications[currentAppId];
        const jobId = currentApp?.application?.job?.id;
        return `/jobs/${jobId}?tab=candidates&appId=${currentAppId}`;
      }
    }
    return `/jobs/${ats_job?.id}?tab=candidates&appId=${ats_application_id}`;
  })();

  // user info and status tags
  const secondLevel = () => {
    return (
      <Flex alignItems="center" gap="8px">
        <Avatar
          src={candidate.avatar}
          name={candidate.name}
          width="24px"
          height="24px"
          className="avatar-sm"
          borderRadius="6px"
        />
        <Link href={candidateLink} target="_blank" outline="none" _focus={{}}>
          <TruncatedTextWithTooltip
            text={candidate.name}
            variant="heading_1"
            maxW={`${availableWidth * 0.5}px`}
          />
        </Link>
        {interview?.transfer_info && (
          <TransferredBadge
            transferInfo={interview?.transfer_info}
            showTooltipToLabel={true}
          />
        )}
        <Icon as={SlashDivider} color="secondary-600" h="20px" />
        <TruncatedTextWithTooltip
          text={interview.name}
          variant="heading_1"
          maxW={`${availableWidth * 0.5}px`}
        />

        <Box ref={intelligenceStatusRef}>
          {isNoteTaker && isBeforeInterview && (
            <KTagWithIcon
              value="AI notetaker enabled"
              icon={Stars02}
              iconProps={{ color: 'pastel-3-dark' }}
              bg="pastel-3-light"
              color="pastel-3-dark"
              whiteSpace="nowrap"
            />
          )}
          {!isBeforeInterview && isNoteTaker && (
            <InterviewIntelligenceStatusTag status={intelligence_status} />
          )}
        </Box>
        <Box ref={interviewStatusRef}>
          <InterviewStatusTag interview={interview} />
        </Box>
        {isBeforeInterview &&
          !isCancelledInterview &&
          dayjs(interview.start_time).diff(now, 'minutes') < 30 &&
          (() => {
            const diffInSeconds = dayjs(interview.start_time).diff(
              now,
              'seconds'
            );
            const mins = Math.ceil(diffInSeconds / 60);
            let label = `Starts in ${mins} minutes`;
            if (diffInSeconds < 3) {
              return null;
            } else if (diffInSeconds < 30) {
              label = 'Starts in a few seconds';
            } else if (diffInSeconds < 60) {
              label = 'Starts in a minute';
            }
            return (
              <KTagWithIcon value={label} icon={Clock} whiteSpace="nowrap" />
            );
          })()}
      </Flex>
    );
  };

  // interview info
  const thirdLevel = () => {
    const { meeting_meta, location } = interview || {};

    const interviewTimeLabel =
      typeof interviewTimeResult === 'string'
        ? interviewTimeResult
        : interviewTimeResult.interviewTimeLabel;

    const videoDetails =
      Array.isArray(meeting_meta) &&
      meeting_meta?.find((item) => item?.entry_point_type === 'video');

    const locationIcon = locationIconMap[location];

    return (
      <Flex pt="12px" alignItems="center">
        {/* job */}
        <Flex alignItems="center" gap="6px">
          <Icon as={LayersThree02} color="secondary-500" />
          <Link
            href={`/jobs/${ats_job?.id}`}
            target="_blank"
            outline="none"
            width="fit-content"
            _focus={{}}
          >
            <KText variant="paragraph" fontWeight="550">
              {ats_job?.name}
            </KText>
          </Link>
        </Flex>

        <Flex
          alignItems="center"
          justifyContent="center"
          p="0 12px"
          color="secondary-900"
          borderRadius="50%"
          fontSize="12px"
          lineHeight="1"
        >
          •
        </Flex>

        {/* location */}
        <Flex alignItems="center" gap="6px">
          {locationIcon?.icon && (
            <Icon as={locationIcon?.icon} color={locationIcon?.color} />
          )}
          <KText
            textTransform="capitalize"
            size="sm-medium"
            color="secondary-700"
          >
            {location?.replace('_', ' ')}
          </KText>
          {videoDetails && (
            <KPopover
              triggerElement={
                <Center>
                  <Icon as={InfoCircle} cursor="pointer" />
                </Center>
              }
              popoverProps={{ trigger: 'hover' }}
              showCloseButton={false}
            >
              <MeetingLinkDetails
                videoDetails={videoDetails}
                location={location}
              />
            </KPopover>
          )}
        </Flex>

        <Flex
          alignItems="center"
          justifyContent="center"
          p="0 12px"
          color="secondary-900"
          borderRadius="50%"
          fontSize="12px"
          lineHeight="1"
        >
          •
        </Flex>

        {/* interview time */}
        <Flex alignItems="center" gap="6px">
          <Icon as={Clock} color="secondary-500" />
          <KText variant="paragraph" fontWeight="550">
            {interviewTimeLabel}
          </KText>
        </Flex>
      </Flex>
    );
  };

  return (
    <>
      <Flex
        p="12px 24px"
        borderBottom="1px solid"
        borderColor="secondary-100"
        boxShadow="2px 4px 10px 0px #D7DAEB1A,8px 17px 19px 0px #D7DAEB17,18px 38px 25px 0px #D7DAEB0D,32px 68px 30px 0px #D7DAEB03,50px 107px 33px 0px #D7DAEB00"
        flexDir="column"
        gap="6px"
        ref={headerRef}
      >
        {firstLevel()}
        {secondLevel()}
        {thirdLevel()}
      </Flex>
      {renderModals()}
    </>
  );
};
